import { Small3 } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import useGraphQl from 'hooks/useGraphQl'
import { FC, useState } from 'react'
import { AccentButton } from '@/components/atoms/button'
import styled from 'styled-components'
import Trans from 'next-translate/Trans'
import Modal from '@/components/organisms/modal'
import { PackageStatus, Sdk } from '@/network/graphql.g'

type PackageStatusButtonProps = {
  orderId: string
  email: string
} & Rest

const PackageStatusButton: FC<PackageStatusButtonProps> = ({
  orderId,
  email,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const { requestNotifyError } = useGraphQl()
  const [showOrderTracking, setShowOrderTracking] = useState<boolean>(false)
  const [packageStatus, setPackageStatus] = useState<PackageStatus>(null)

  const orderTrackingClick = async () => {
    const result = await requestNotifyError((sdk: Sdk) => ({
      method: sdk.getPackageStatus,
      variables: { orderId, email: email ?? '' }
    }))
    if (result.success) {
      setPackageStatus(result?.response?.packageStatus)
      setShowOrderTracking(true)
    } else {
      setPackageStatus(null)
      setShowOrderTracking(false)
    }
  }

  return (
    <>
      <StyledAccentButton
        mb={{ desktop: 0, tablet: 0, mobile: 12 }}
        width={{ desktop: 'auto', tablet: 'auto', mobile: '100%' }}
        onClick={orderTrackingClick}
        {...rest}
      >
        {t('Account.Orders.Detail.deliveryButton')}
      </StyledAccentButton>
      {showOrderTracking && (
        <Modal
          onDidTapClose={() => setShowOrderTracking(false)}
          title={t('Account.Orders.Detail.Tracking.title')}
          description={
            <>
              {packageStatus.state && (
                <Small3 textAlign="center">
                  {t('Account.Orders.Detail.Tracking.state', {
                    state: packageStatus.state
                  })}
                </Small3>
              )}
              {packageStatus.trackingNumber && packageStatus.trackingLink ? (
                <StyledTrackingContent>
                  <Trans
                    i18nKey="common:Account.Orders.Detail.Tracking.content"
                    components={[
                      <StyledTrackingNumber />,
                      <a target="_blank" href={packageStatus.trackingLink} />
                    ]}
                    values={{
                      trackingNumber: packageStatus.trackingNumber,
                      trackingLink: packageStatus.trackingLink
                    }}
                  />
                </StyledTrackingContent>
              ) : (
                t('Account.Orders.Detail.Tracking.error')
              )}
            </>
          }
        />
      )}
    </>
  )
}

export default PackageStatusButton

const StyledAccentButton = styled(AccentButton)`
  text-transform: uppercase;
`

const StyledTrackingNumber = styled(Small3)`
  width: 100%;
  margin: 0;
  padding: 0;
  text-align: center;
  a {
    text-decoration: none;
    color: ${(props) => props.theme.colors.onBackground};
  }
  a:hover {
    text-decoration: underline;
  }
`

const StyledTrackingContent = styled.div`
  text-align: center;
`
